'use client';

import React from 'react';
import { Mail, MessageSquare, Shield, Clock, ChevronRight } from 'lucide-react';
import PolicyLayout, {
  PolicySection,
  PolicySubSection,
  PolicyList,
  PolicyCallout,
  TocSection,
} from '@/components/PolicyLayout';
import Icon from '@/components/ui/AppIcon';



const sections: TocSection[] = [
  { id: 'support', title: 'General Support' },
  { id: 'privacy-requests', title: 'Privacy Requests' },
  { id: 'grievance', title: 'Grievance Reporting' },
  { id: 'response-times', title: 'Response Expectations' },
  { id: 'escalation', title: 'Escalation' },
];

function ContactCard({
  icon: Icon,
  iconBg,
  iconColor,
  title,
  email,
  description,
  responseTime,
}: {
  icon: React.ElementType;
  iconBg: string;
  iconColor: string;
  title: string;
  email: string;
  description: string;
  responseTime: string;
}) {
  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-start gap-3 mb-3">
        <div className={`w-10 h-10 rounded-xl ${iconBg} flex items-center justify-center shrink-0`}>
          <Icon size={18} className={iconColor} />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
        </div>
      </div>
      <div className="space-y-2">
        <div className="flex items-center gap-2 px-3 py-2 bg-muted/40 rounded-lg">
          <Mail size={13} className="text-muted-foreground shrink-0" />
          <span className="text-xs text-foreground font-medium">{email}</span>
        </div>
        <div className="flex items-center gap-2 px-3 py-2 bg-muted/40 rounded-lg">
          <Clock size={13} className="text-muted-foreground shrink-0" />
          <span className="text-xs text-muted-foreground">{responseTime}</span>
        </div>
      </div>
    </div>
  );
}

export default function ContactGrievanceClient() {
  return (
    <PolicyLayout
      title="Contact & Grievance"
      description="How to reach JobCompanion for support, privacy requests, safety reports, and formal grievances."
      lastUpdated="May 18, 2026"
      icon={Mail}
      iconBg="bg-success/10"
      iconColor="text-success"
      sections={sections}
      currentPath="/contact-grievance"
    >
      <PolicyCallout type="info">
        JobCompanion is in controlled beta. Our support team is small but responsive. We aim to reply to all inquiries within 2–5 business days.
      </PolicyCallout>

      <PolicySection id="support" title="General Support">
        <p>
          For general questions, technical issues, feature requests, or account help, contact our support team:
        </p>
        <div className="mt-4">
          <ContactCard
            icon={MessageSquare}
            iconBg="bg-primary/10"
            iconColor="text-primary"
            title="General Support"
            email="support@jobcompanion.app"
            description="Technical issues, account questions, feature feedback"
            responseTime="Response within 2–5 business days"
          />
        </div>
        <div className="mt-4 space-y-2 text-sm text-muted-foreground">
          <p>When contacting support, please include:</p>
          <PolicyList items={[
            'Your account email address',
            'A clear description of the issue or question',
            'Steps to reproduce the issue (for technical bugs)',
            'Screenshots or error messages if applicable',
          ]} />
        </div>
      </PolicySection>

      <PolicySection id="privacy-requests" title="Privacy Requests">
        <p>
          For privacy-related requests including data access, correction, deletion, or portability, contact our privacy team:
        </p>
        <div className="mt-4">
          <ContactCard
            icon={Shield}
            iconBg="bg-accent/10"
            iconColor="text-accent"
            title="Privacy Requests"
            email="privacy@jobcompanion.app"
            description="Data access, deletion, correction, portability requests"
            responseTime="Response within 5 business days, action within 30 days"
          />
        </div>
        <PolicySubSection title="How to Submit a Privacy Request">
          <p>Email us with subject line: <strong>&quot;Privacy Request — [Type]&quot;</strong> (e.g., &quot;Privacy Request — Data Deletion&quot;)</p>
          <p>Include in your email:</p>
          <PolicyList items={[
            'Your full name and account email address',
            'The type of request (access, deletion, correction, portability)',
            'Specific data you are requesting action on (if applicable)',
            'Your preferred contact method for follow-up',
          ]} />
        </PolicySubSection>
        <PolicySubSection title="Identity Verification">
          <p>
            To protect your privacy, we will verify your identity before processing any privacy request. We may ask you to confirm details associated with your account.
          </p>
        </PolicySubSection>
        <p>
          See our full <a href="/privacy-policy" className="text-primary underline">Privacy Policy</a> and <a href="/data-retention" className="text-primary underline">Data Retention Policy</a> for more details on your rights.
        </p>
      </PolicySection>

      <PolicySection id="grievance" title="Grievance Reporting">
        <p>
          If you have a formal grievance about JobCompanion&apos;s practices, policies, or conduct, you can submit a grievance report:
        </p>
        <div className="mt-4">
          <ContactCard
            icon={Shield}
            iconBg="bg-error/10"
            iconColor="text-error"
            title="Grievance & Safety Reports"
            email="safety@jobcompanion.app"
            description="Policy violations, safety concerns, formal complaints"
            responseTime="Acknowledgment within 2 business days, resolution within 15 days"
          />
        </div>
        <PolicySubSection title="What Qualifies as a Grievance">
          <PolicyList items={[
            'Suspected violations of our Terms & Conditions or Acceptable Use Policy',
            'Safety concerns about platform features or automation behavior',
            'Complaints about how your data has been handled',
            'Reports of fraudulent job listings or suspicious employer activity',
            'Intellectual property infringement claims',
            'Complaints about account suspension or enforcement actions',
            'Any formal complaint about JobCompanion\'s conduct',
          ]} />
        </PolicySubSection>
        <PolicySubSection title="Grievance Process">
          <PolicyList items={[
            'Submit your grievance via email with a clear description of the issue',
            'We will acknowledge receipt within 2 business days',
            'A member of our team will investigate and respond within 15 business days',
            'If additional information is needed, we will contact you',
            'You will receive a written response with our findings and any actions taken',
          ]} />
        </PolicySubSection>
      </PolicySection>

      <PolicySection id="response-times" title="Response Expectations">
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 pr-4 text-xs font-semibold text-foreground">Request Type</th>
                <th className="text-left py-2 pr-4 text-xs font-semibold text-foreground">Acknowledgment</th>
                <th className="text-left py-2 text-xs font-semibold text-foreground">Resolution</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {[
                ['General Support', '1–2 business days', '2–5 business days'],
                ['Technical Bug Report', '1 business day', '5–10 business days'],
                ['Privacy Request', '2–3 business days', '30 days (legal requirement)'],
                ['Data Deletion Request', '2–3 business days', '30 days'],
                ['Grievance Report', '2 business days', '15 business days'],
                ['IP Infringement Notice', '3 business days', '10 business days'],
                ['Account Suspension Appeal', '2 business days', '10 business days'],
              ].map(([type, ack, res]) => (
                <tr key={type}>
                  <td className="py-2 pr-4 text-xs text-foreground">{type}</td>
                  <td className="py-2 pr-4 text-xs text-muted-foreground">{ack}</td>
                  <td className="py-2 text-xs text-muted-foreground">{res}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          Response times are targets, not guarantees. During high-volume periods or complex investigations, resolution may take longer. We will keep you informed of any delays.
        </p>
      </PolicySection>

      <PolicySection id="escalation" title="Escalation">
        <p>
          If you are not satisfied with our response to a grievance or privacy request, you may:
        </p>
        <PolicyList items={[
          'Request escalation to a senior team member by replying to our response email',
          'Contact your local data protection authority (for GDPR-related complaints in the EU)',
          'Seek independent legal advice',
        ]} />
        <p>
          We are committed to resolving all complaints fairly and transparently. If you believe we have not adequately addressed your concern, please let us know and we will make every effort to find a satisfactory resolution.
        </p>
        <div className="mt-4 bg-primary/5 border border-primary/15 rounded-xl p-4">
          <p className="text-sm font-semibold text-foreground mb-2">Quick Contact Summary</p>
          <div className="space-y-1.5">
            {[
              ['General Support', 'support@jobcompanion.app'],
              ['Privacy Requests', 'privacy@jobcompanion.app'],
              ['Safety & Grievances', 'safety@jobcompanion.app'],
              ['Legal & IP', 'legal@jobcompanion.app'],
              ['Billing', 'billing@jobcompanion.app'],
            ].map(([label, email]) => (
              <div key={label} className="flex items-center gap-2">
                <ChevronRight size={11} className="text-primary shrink-0" />
                <span className="text-xs text-foreground font-medium w-36">{label}:</span>
                <a href={`mailto:${email}`} className="text-xs text-primary underline">{email}</a>
              </div>
            ))}
          </div>
        </div>
      </PolicySection>
    </PolicyLayout>
  );
}
